import { useState, useEffect, useMemo } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { Plus, Trash2, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/*
 * config = {
 *   adapter,                 // { list, create, remove } from supabaseAdapter
 *   filterBy,                // "profile_id" | "profile_name" — column used for the dropdown filter
 *   eyebrow, title, description, addLabel, icon, accent, testIdPrefix,
 *   blank: () => ({...}),
 *   transform: (form, { profile }) => payload,
 *   fields: [{ key, label, type, required, options, span, placeholder, step }],
 *   render: (item) => JSX,
 * }
 */
export default function ResourcePage({ config }) {
  const { profiles } = useOutletContext();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(config.blank());
  const [filter, setFilter] = useState("all");
  const [error, setError] = useState("");

  const filterBy = config.filterBy || "profile_id";

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      // If filter is by profile_name, the dropdown value is profile.id; convert to name.
      let filterValue = filter;
      if (filter !== "all" && filterBy === "profile_name") {
        const p = profiles.find((x) => x.id === filter);
        filterValue = p?.name || filter;
      }
      const data = await config.adapter.list({ filterValue });
      setItems(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, profiles.length]);

  useEffect(() => {
    if (!form.profile_id && profiles?.length > 0) {
      setForm((f) => ({ ...f, profile_id: profiles[0].id }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profiles]);

  const profileMap = useMemo(() => {
    const m = {};
    (profiles || []).forEach((p) => {
      m[p.id] = p;
      m[p.name] = p;
    });
    return m;
  }, [profiles]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const profile = profiles.find((p) => p.id === form.profile_id);
      const payload = config.transform ? config.transform(form, { profile }) : form;
      await config.adapter.create(payload);
      setForm({ ...config.blank(), profile_id: form.profile_id });
      setOpen(false);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await config.adapter.remove(id);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const noProfiles = !profiles || profiles.length === 0;

  return (
    <div data-testid={`${config.testIdPrefix}-page`} className="mx-auto max-w-6xl space-y-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <span className="text-xs font-semibold uppercase tracking-wider text-accent">
            {config.eyebrow}
          </span>
          <h1 className="mt-1 font-heading text-3xl font-extrabold tracking-tight">
            {config.title}
          </h1>
          <p className="mt-1 max-w-md text-sm text-muted-foreground">{config.description}</p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          {profiles?.length > 1 && (
            <div className="flex items-center gap-2 rounded-xl bg-card px-3 py-1.5 shadow-soft">
              <Filter className="h-3.5 w-3.5 text-muted-foreground" />
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger
                  className="h-7 border-0 bg-transparent text-sm shadow-none"
                  data-testid={`${config.testIdPrefix}-filter-profile`}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All profiles</SelectItem>
                  {profiles.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {config.extraAction && config.extraAction({ profiles, refresh: load })}
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                size="lg"
                disabled={noProfiles}
                data-testid={`${config.testIdPrefix}-add-btn`}
              >
                <Plus className="mr-1 h-4 w-4" /> {config.addLabel}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{config.addLabel}</DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label>For whom *</Label>
                  <Select
                    value={form.profile_id || ""}
                    onValueChange={(v) => setForm({ ...form, profile_id: v })}
                  >
                    <SelectTrigger data-testid={`${config.testIdPrefix}-profile-select`}>
                      <SelectValue placeholder="Select a profile" />
                    </SelectTrigger>
                    <SelectContent>
                      {profiles.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {config.fields.map((f) => (
                    <FieldRenderer
                      key={f.key}
                      field={f}
                      form={form}
                      setForm={setForm}
                      testIdPrefix={config.testIdPrefix}
                    />
                  ))}
                </div>
                {error && (
                  <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                    {error}
                  </div>
                )}
                <DialogFooter>
                  <Button type="submit" data-testid={`${config.testIdPrefix}-submit`}>
                    Save
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {error && !open && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      {noProfiles ? (
        <div
          className="flex flex-col items-center justify-center rounded-3xl border-2 border-dashed border-border bg-card/50 p-16 text-center"
          data-testid={`${config.testIdPrefix}-needs-profile`}
        >
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-secondary">
            <config.icon className="h-7 w-7 text-primary" />
          </div>
          <h3 className="mt-4 font-heading text-xl font-bold">Add a profile first</h3>
          <p className="mt-2 max-w-sm text-sm text-muted-foreground">
            {config.title} are tracked per family member.
          </p>
          <Link to="/profiles">
            <Button className="mt-6">Go to profiles</Button>
          </Link>
        </div>
      ) : loading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-32 animate-pulse rounded-2xl bg-card shadow-soft" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div
          className="flex flex-col items-center justify-center rounded-3xl border-2 border-dashed border-border bg-card/50 p-16 text-center"
          data-testid={`${config.testIdPrefix}-empty`}
        >
          <div className={`flex h-16 w-16 items-center justify-center rounded-2xl ${config.accent}`}>
            <config.icon className="h-7 w-7" />
          </div>
          <h3 className="mt-4 font-heading text-xl font-bold">No entries yet</h3>
          <p className="mt-2 max-w-sm text-sm text-muted-foreground">
            Add your first {config.title.toLowerCase().slice(0, -1)} to start tracking.
          </p>
          <Button onClick={() => setOpen(true)} className="mt-6">
            <Plus className="mr-1 h-4 w-4" /> {config.addLabel}
          </Button>
        </div>
      ) : (
        <div
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
          data-testid={`${config.testIdPrefix}-list`}
        >
          {items.map((item) => {
            const linkedProfile =
              (item.profile_id && profileMap[item.profile_id]) ||
              (item.profile_name && profileMap[item.profile_name]);
            return (
              <div
                key={item.id}
                className="group relative flex flex-col rounded-2xl bg-card p-6 shadow-soft transition-shadow hover:shadow-card"
                data-testid={`${config.testIdPrefix}-card-${item.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${config.accent}`}>
                    <config.icon className="h-5 w-5" />
                  </div>
                  <button
                    onClick={() => remove(item.id)}
                    className="rounded-lg p-2 text-muted-foreground opacity-0 transition hover:bg-destructive/10 hover:text-destructive group-hover:opacity-100"
                    data-testid={`${config.testIdPrefix}-delete-${item.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <div className="mt-4 flex-1">{config.render(item)}</div>
                <div className="mt-4 border-t border-border pt-3">
                  <span className="text-xs text-muted-foreground">
                    For {linkedProfile?.name || item.profile_name || "—"}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function FieldRenderer({ field, form, setForm, testIdPrefix }) {
  const v = form[field.key] ?? "";
  const set = (val) => setForm({ ...form, [field.key]: val });
  const span = field.span === 2 ? "col-span-2" : "";
  const testId = `${testIdPrefix}-field-${field.key}`;

  if (field.type === "textarea") {
    return (
      <div className={span || "col-span-2"}>
        <Label>{field.label}{field.required ? " *" : ""}</Label>
        <Textarea
          data-testid={testId}
          value={v}
          required={field.required}
          rows={3}
          onChange={(e) => set(e.target.value)}
          placeholder={field.placeholder}
        />
      </div>
    );
  }
  if (field.type === "select") {
    return (
      <div className={span}>
        <Label>{field.label}{field.required ? " *" : ""}</Label>
        <Select value={v} onValueChange={set}>
          <SelectTrigger data-testid={testId}>
            <SelectValue placeholder={field.placeholder || "Select"} />
          </SelectTrigger>
          <SelectContent>
            {field.options.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }
  return (
    <div className={span}>
      <Label>{field.label}{field.required ? " *" : ""}</Label>
      <Input
        data-testid={testId}
        type={field.type || "text"}
        value={v}
        required={field.required}
        step={field.step}
        onChange={(e) => set(e.target.value)}
        placeholder={field.placeholder}
      />
    </div>
  );
}
